import {VFC} from 'react';
import {format} from 'date-fns';
import Button from "@material-ui/core/Button";
import {getDateDiff} from "../../utils/dateUtils";
import styled from "@emotion/styled";
import {OrderProduct} from "../../reducks/products/types";
import {ButtonBase} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {Order} from "../../reducks/orders/types";

type Props = {
  order: Order;
  onCancelOrder: (order: Order) => void;
};

/**
 * Date型の日時を日付と時刻の文字列に変換する.
 * 
 * @param date - Date型の日時
 * @returns 文字列に変換した日時
 */
const dateTimeToString = (date: Date): string => {
  return format(date, 'yyyy年 MM月 dd日 HH:mm');
};

/**
 * Date型の日時を日付のみ文字列に変換する
 * 
 * @param date - Date型の日時
 * @returns 文字列に変換した日付
 */

const OutlinedCard = styled.div`
  border-radius: 10px;
  border-width: 1px;
  border-color: #857371;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  padding: 1rem;
`

const Caption = styled.p`
  color: #857371;
  font-size: 0.8rem;
`

const Expanded = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`

const Thumbnail = styled.img`
  border-radius: 10px;
  object-fit: cover;
  aspect-ratio: 1/1;
  width: auto;
  height: 100%;
`

const ProductGridItem = (props: {
  product: OrderProduct
}) => {
  const product = props.product;
  const thumbnailSrc = product.images.length > 0 ? product.images[0].path : "./img/no_image.png";
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/product/detail/${product.id}`)
  }

  return <div className={"flex flex-col gap-1 items-start self-stretch"}>
    <ButtonBase style={{ borderRadius: "10px" }} onClick={handleClick}>
      <Thumbnail src={thumbnailSrc} alt={"product-thumbnail"}/>
    </ButtonBase>
    <div className={"flex flex-col gap-0 items-start self-stretch"}>
      <p className={"text-sm"}>
        {product.name}
      </p>
      <Caption>
        ¥{product.price} × {product.quantity}
      </Caption>
    </div>
  </div>
}

const InfoRow = (props: {label: string, value: string}) => {
  return <Expanded>
    <Caption>
      {props.label}
    </Caption>
    <p className={"text-base"}>
      {props.value}
    </p>
  </Expanded>
}

export const OrderHistoryItem: VFC<Props> = (props) => {
  const { order } = props;

  // 注文日時
  const orderedDateTime = dateTimeToString(order.created_at.toDate());
  // 受け取り日
  const receiveDate = dateTimeToString(order.received_Timestamp.toDate());
  // 注文の合計金額
  const price = `¥${order.amount.toLocaleString()}`;

  const today = new Date();
  const canCancel = getDateDiff(today, order.received_Timestamp.toDate()) <= -1;
  const isCanceled = order.status === "canceled";

  return (
    <OutlinedCard>
      <div className={"flex flex-col gap-1.5"}>
        <InfoRow label={"注文日時"} value={orderedDateTime}/>
        <InfoRow label={"受取日時"} value={receiveDate}/>
      </div>
      <div className={"grid grid-cols-3 gap-2 gap-y-4"}>
        {order.products.map(p => <ProductGridItem key={p.id} product={p}/>)}
      </div>
      <Expanded>
        <div className={"flex flex-row gap-3 items-center"}>
          <p className={"text-base"}>
            合計
          </p>
          <p className={"text-2xl"}>
            {price}
          </p>
        </div>
        <Button variant={"contained"} disabled={!canCancel || isCanceled} onClick={() => props.onCancelOrder(order)} color={"primary"}>
          {isCanceled ? "キャンセル済み" : canCancel ? "注文をキャンセル" : "キャンセルできません"}
        </Button>
      </Expanded>
    </OutlinedCard>
  );
};
